import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Chip, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, Motorcycle, CheckCircle, XCircle, Clock, Eye, MagnifyingGlass, House, LinkSimple, LinkBreak } from "@phosphor-icons/react";
import { toast } from "sonner";
import { APPROVAL_STATUS_LABELS, APPROVAL_STATUS_VARIANTS } from "@/lib/parkingTypes";

// Gate/Security vehicle-entry approval + blacklist tool. This is the SAME page/data/RBAC that
// previously lived at Community -> "Vehicle Management" (backed by the `vehicles` collection,
// entirely separate from the Vehicle Management V2 registry which is built on `parking_vehicles`)
// — re-homed here under Gate so it isn't lost when Vehicle Management V2 took over that route.
export default function GateVehicleApproval() {
    const [vehicles, setVehicles] = useState([]);
    const [filteredVehicles, setFilteredVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [approvalDialog, setApprovalDialog] = useState(null);
    const [approvalForm, setApprovalForm] = useState({ status: "approved", remarks: "" });
    const [communityVehicles, setCommunityVehicles] = useState([]);
    const [selectedLinkId, setSelectedLinkId] = useState("");
    const [linking, setLinking] = useState(false);
    const [rfidTagInput, setRfidTagInput] = useState("");
    const [savingTag, setSavingTag] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const response = await api.get("/vehicles");
            setVehicles(response.data);
            setFilteredVehicles(response.data);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    // Vehicle Approval & Domain Bridge V2 — when the review/detail dialog opens, load this
    // flat's Community `parking_vehicles` (approved only) so admin/committee can pick one to
    // link. Purely for the picker; never auto-selects or auto-matches by registration number.
    useEffect(() => {
        setSelectedLinkId("");
        setRfidTagInput(approvalDialog?.rfid_tag_id || "");
        if (!approvalDialog?.flat_id) { setCommunityVehicles([]); return; }
        let cancelled = false;
        api.get(`/flats/${approvalDialog.flat_id}/parking-vehicles`).then(({ data }) => {
            if (cancelled) return;
            setCommunityVehicles((data || []).filter((pv) => (pv.approval_status || "approved") === "approved"));
        }).catch((e) => { if (!cancelled) toast.error(formatError(e)); });
        return () => { cancelled = true; };
    }, [approvalDialog?.id, approvalDialog?.flat_id]);

    async function handleLink() {
        if (!selectedLinkId) return;
        setLinking(true);
        try {
            const { data } = await api.post(`/vehicles/${approvalDialog.id}/link-parking-vehicle`, { parking_vehicle_id: selectedLinkId });
            toast.success("Linked to community vehicle");
            setApprovalDialog(data);
            setVehicles((rows) => rows.map((v) => (v.id === data.id ? data : v)));
        } catch (e) { toast.error(formatError(e)); } finally { setLinking(false); }
    }

    async function handleUnlink() {
        setLinking(true);
        try {
            const { data } = await api.post(`/vehicles/${approvalDialog.id}/link-parking-vehicle`, { parking_vehicle_id: null });
            toast.success("Unlinked from community vehicle");
            setApprovalDialog(data);
            setVehicles((rows) => rows.map((v) => (v.id === data.id ? data : v)));
        } catch (e) { toast.error(formatError(e)); } finally { setLinking(false); }
    }

    async function handleSaveTag() {
        setSavingTag(true);
        try {
            const { data } = await api.put(`/vehicles/${approvalDialog.id}`, { rfid_tag_id: rfidTagInput.trim().toUpperCase() || "" });
            toast.success("RFID tag saved");
            setApprovalDialog(data);
            setVehicles((rows) => rows.map((v) => (v.id === data.id ? data : v)));
        } catch (e) { toast.error(formatError(e)); } finally { setSavingTag(false); }
    }

    useEffect(() => {
        let filtered = vehicles;

        // Filter by status
        if (filterStatus !== "all") {
            filtered = filtered.filter(v => v.status === filterStatus);
        }

        // Search
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(v => 
                v.vehicle_number.toLowerCase().includes(query) ||
                v.owner_name.toLowerCase().includes(query) ||
                (v.flat && `${v.flat.block}-${v.flat.number}`.toLowerCase().includes(query))
            );
        }

        setFilteredVehicles(filtered);
    }, [searchQuery, filterStatus, vehicles]);

    async function handleApproval(e) {
        e.preventDefault();
        try {
            const { data } = await api.post(`/vehicles/${approvalDialog.id}/approve`, approvalForm);
            if (approvalForm.status === "approved" && data.sync_warning) {
                toast.warning(data.sync_warning, { duration: 8000 });
            } else {
                toast.success(`Vehicle ${approvalForm.status === "approved" ? "approved" : "rejected"} successfully`);
            }
            setApprovalDialog(null);
            setApprovalForm({ status: "approved", remarks: "" });
            load();
        } catch (e) {
            toast.error(formatError(e));
        }
    }

    const getStatusChip = (status) => {
        switch (status) {
            case "approved":
                return <Chip color="green" icon={<CheckCircle size={14} />}>Approved</Chip>;
            case "rejected":
                return <Chip color="red" icon={<XCircle size={14} />}>Rejected</Chip>;
            case "pending":
            default:
                return <Chip color="yellow" icon={<Clock size={14} />}>Pending</Chip>;
        }
    };

    const pendingCount = vehicles.filter(v => v.status === "pending").length;
    const approvedCount = vehicles.filter(v => v.status === "approved").length;
    const rejectedCount = vehicles.filter(v => v.status === "rejected").length;

    if (loading) return <LoadingState />;

    return (
        <div className="animate-fade-up">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white border border-brand-line rounded-sm p-4">
                    <p className="text-sm text-brand-inkSoft mb-1">Total Vehicles</p>
                    <p className="text-3xl font-heading font-bold text-brand-ink">{vehicles.length}</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4">
                    <p className="text-sm text-yellow-700 mb-1">Pending Approval</p>
                    <p className="text-3xl font-heading font-bold text-yellow-800">{pendingCount}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-sm p-4">
                    <p className="text-sm text-green-700 mb-1">Approved</p>
                    <p className="text-3xl font-heading font-bold text-green-800">{approvedCount}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                    <p className="text-sm text-red-700 mb-1">Rejected</p>
                    <p className="text-3xl font-heading font-bold text-red-800">{rejectedCount}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                        <MagnifyingGlass size={20} className="text-brand-inkSoft" />
                        <Input
                            data-testid="gate-vehicle-search-input"
                            placeholder="Search by vehicle number, owner, or flat..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="flex-1"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant={filterStatus === "all" ? "default" : "outline"}
                            onClick={() => setFilterStatus("all")}
                            className="flex-1"
                        >
                            All ({vehicles.length})
                        </Button>
                        <Button
                            size="sm"
                            variant={filterStatus === "pending" ? "default" : "outline"}
                            onClick={() => setFilterStatus("pending")}
                            className="flex-1"
                        >
                            Pending ({pendingCount})
                        </Button>
                        <Button
                            size="sm"
                            variant={filterStatus === "approved" ? "default" : "outline"}
                            onClick={() => setFilterStatus("approved")}
                            className="flex-1"
                        >
                            Approved ({approvedCount})
                        </Button>
                    </div>
                </div>
            </div>

            {/* Vehicles Table */}
            <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-brand-sage/30">
                            <tr className="text-left">
                                <th className="p-3 text-xs font-medium text-brand-ink">Vehicle</th>
                                <th className="p-3 text-xs font-medium text-brand-ink">Owner</th>
                                <th className="p-3 text-xs font-medium text-brand-ink">Flat</th>
                                <th className="p-3 text-xs font-medium text-brand-ink">Details</th>
                                <th className="p-3 text-xs font-medium text-brand-ink">Pass Number</th>
                                <th className="p-3 text-xs font-medium text-brand-ink">Parking</th>
                                <th className="p-3 text-xs font-medium text-brand-ink">Status</th>
                                <th className="p-3 text-xs font-medium text-brand-ink">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVehicles.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="p-8 text-center text-brand-inkSoft">
                                        No vehicles found
                                    </td>
                                </tr>
                            ) : (
                                filteredVehicles.map((vehicle, i) => (
                                    <tr key={vehicle.id} data-testid={`gate-vehicle-row-${vehicle.vehicle_number}`} className={i % 2 === 0 ? "bg-white" : "bg-brand-sage/10"}>
                                        <td className="p-3">
                                            <div className="flex items-center gap-3">
                                                {vehicle.type === "2-wheeler" ? (
                                                    <Motorcycle size={24} className="text-blue-600" />
                                                ) : (
                                                    <Car size={24} className="text-green-600" />
                                                )}
                                                <div>
                                                    <p className="font-semibold text-brand-ink">{vehicle.vehicle_number}</p>
                                                    <p className="text-xs text-brand-inkSoft">{vehicle.type}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 text-sm text-brand-ink">{vehicle.owner_name}</td>
                                        <td className="p-3">
                                            {vehicle.flat ? (
                                                <div className="flex items-center gap-1">
                                                    <House size={14} className="text-brand-inkSoft" />
                                                    <span className="text-sm font-medium text-brand-ink">
                                                        {vehicle.flat.block}-{vehicle.flat.number}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-brand-inkSoft">—</span>
                                            )}
                                        </td>
                                        <td className="p-3 text-sm text-brand-inkSoft">
                                            {vehicle.make && vehicle.model ? `${vehicle.make} ${vehicle.model}` : "—"}
                                            {vehicle.color && <span className="block text-xs">({vehicle.color})</span>}
                                        </td>
                                        <td className="p-3">
                                            <span className="font-mono text-xs text-brand-ink">
                                                {vehicle.parking_pass_number || "—"}
                                            </span>
                                        </td>
                                        <td className="p-3 text-sm">
                                            {vehicle.parking_slot ? (
                                                <span className="font-semibold text-green-600">
                                                    {vehicle.parking_slot.slot_number}
                                                </span>
                                            ) : (
                                                <span className="text-brand-inkSoft">Not assigned</span>
                                            )}
                                        </td>
                                        <td className="p-3">{getStatusChip(vehicle.status)}</td>
                                        <td className="p-3">
                                            {vehicle.status === "pending" ? (
                                                <Button
                                                    size="sm"
                                                    data-testid={`gate-vehicle-review-btn-${vehicle.vehicle_number}`}
                                                    onClick={() => setApprovalDialog(vehicle)}
                                                    className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white"
                                                >
                                                    Review
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setApprovalDialog(vehicle)}
                                                    className="rounded-full"
                                                >
                                                    <Eye size={14} className="mr-1" />
                                                    View
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Approval Dialog */}
            <Dialog open={!!approvalDialog} onOpenChange={() => setApprovalDialog(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            {approvalDialog?.status === "pending" ? "Review Vehicle" : "Vehicle Details"}
                        </DialogTitle>
                    </DialogHeader>
                    {approvalDialog && (
                        <div className="space-y-4">
                            {/* Vehicle Details */}
                            <div className="grid grid-cols-2 gap-4 p-4 bg-brand-sage/10 rounded-sm">
                                <div>
                                    <p className="text-xs text-brand-inkSoft mb-1">Vehicle Number</p>
                                    <p className="font-semibold text-brand-ink">{approvalDialog.vehicle_number}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-brand-inkSoft mb-1">Type</p>
                                    <p className="font-medium text-brand-ink">{approvalDialog.type}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-brand-inkSoft mb-1">Owner</p>
                                    <p className="font-medium text-brand-ink">{approvalDialog.owner_name}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-brand-inkSoft mb-1">Flat</p>
                                    <p className="font-medium text-brand-ink">
                                        {approvalDialog.flat ? `${approvalDialog.flat.block}-${approvalDialog.flat.number}` : "—"}
                                    </p>
                                </div>
                                {approvalDialog.make && (
                                    <div>
                                        <p className="text-xs text-brand-inkSoft mb-1">Make/Model</p>
                                        <p className="font-medium text-brand-ink">{approvalDialog.make} {approvalDialog.model}</p>
                                    </div>
                                )}
                                {approvalDialog.color && (
                                    <div>
                                        <p className="text-xs text-brand-inkSoft mb-1">Color</p>
                                        <p className="font-medium text-brand-ink">{approvalDialog.color}</p>
                                    </div>
                                )}
                            </div>

                            {/* Vehicle Approval & Domain Bridge V2 — optional Community link */}
                            <div className="p-4 bg-white border border-brand-line rounded-sm space-y-3" data-testid="gate-community-link-section">
                                <p className="text-xs font-semibold text-brand-ink uppercase tracking-wide">Community Vehicle Link (optional)</p>
                                {approvalDialog.linked_parking_vehicle ? (
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <div>
                                            <p className="text-sm font-mono font-medium text-brand-ink flex items-center gap-2">
                                                <LinkSimple size={14} /> {approvalDialog.linked_parking_vehicle.registration_number}
                                            </p>
                                            <p className="text-xs text-brand-inkSoft mt-0.5">Linked to this flat's Community Vehicle Management registry</p>
                                        </div>
                                        <Button size="sm" variant="outline" onClick={handleUnlink} disabled={linking} data-testid="gate-unlink-parking-vehicle-btn" className="rounded-full">
                                            <LinkBreak size={14} className="mr-1.5" /> Unlink
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <Select value={selectedLinkId} onValueChange={setSelectedLinkId} disabled={communityVehicles.length === 0}>
                                            <SelectTrigger data-testid="gate-link-parking-vehicle-select" className="flex-1">
                                                <SelectValue placeholder={communityVehicles.length ? "Select an approved community vehicle" : "No approved community vehicles for this flat"} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {communityVehicles.map((pv) => <SelectItem key={pv.id} value={pv.id}>{pv.registration_number}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <Button size="sm" onClick={handleLink} disabled={!selectedLinkId || linking} data-testid="gate-link-parking-vehicle-btn"
                                            className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white shrink-0">
                                            <LinkSimple size={14} className="mr-1.5" /> Link
                                        </Button>
                                    </div>
                                )}
                                <p className="text-[11px] text-brand-inkSoft">
                                    Auto-linked the moment this vehicle is approved for gate access — approving above also creates/approves the matching Community Vehicle Management record automatically. Use this only to override or unlink manually.
                                </p>
                            </div>

                            {/* RFID Tag — boom-barrier access */}
                            <div className="p-4 bg-white border border-brand-line rounded-sm space-y-3" data-testid="gate-rfid-tag-section">
                                <p className="text-xs font-semibold text-brand-ink uppercase tracking-wide">RFID Tag (Boom Barrier Access)</p>
                                <div className="flex items-center gap-2">
                                    <Input
                                        placeholder="e.g. E200400112233"
                                        value={rfidTagInput}
                                        data-testid="gate-rfid-tag-input"
                                        onChange={(e) => setRfidTagInput(e.target.value.toUpperCase())}
                                        className="font-mono flex-1"
                                    />
                                    <Button size="sm" onClick={handleSaveTag} disabled={savingTag} data-testid="gate-rfid-tag-save-btn"
                                        className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white shrink-0">
                                        {savingTag ? "Saving…" : "Save Tag"}
                                    </Button>
                                </div>
                                <p className="text-[11px] text-brand-inkSoft">
                                    Issue this vehicle's physical RFID tag number so IN/OUT readers open the boom barrier automatically — only works once this vehicle is Approved and not blacklisted.
                                </p>
                            </div>

                            {approvalDialog.status === "pending" ? (
                                <form onSubmit={handleApproval} className="space-y-4">
                                    <div className="flex gap-4">
                                        <Button
                                            type="button"
                                            data-testid="gate-approve-option-btn"
                                            variant={approvalForm.status === "approved" ? "default" : "outline"}
                                            onClick={() => setApprovalForm({ ...approvalForm, status: "approved" })}
                                            className="flex-1"
                                        >
                                            <CheckCircle size={16} className="mr-2" />
                                            Approve
                                        </Button>
                                        <Button
                                            type="button"
                                            data-testid="gate-reject-option-btn"
                                            variant={approvalForm.status === "rejected" ? "default" : "outline"}
                                            onClick={() => setApprovalForm({ ...approvalForm, status: "rejected" })}
                                            className="flex-1"
                                        >
                                            <XCircle size={16} className="mr-2" />
                                            Reject
                                        </Button>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">Remarks (Optional)</label>
                                        <Textarea
                                            placeholder="Add any notes or reason for rejection..."
                                            value={approvalForm.remarks}
                                            onChange={(e) => setApprovalForm({ ...approvalForm, remarks: e.target.value })}
                                            rows={3}
                                        />
                                    </div>

                                    <DialogFooter>
                                        <Button type="button" variant="outline" onClick={() => setApprovalDialog(null)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit" data-testid="gate-approval-submit-btn" className="bg-brand-action hover:bg-brand-actionHover text-white">
                                            Submit
                                        </Button>
                                    </DialogFooter>
                                </form>
                            ) : (
                                <div>
                                    <div className="p-4 bg-brand-sage/10 rounded-sm">
                                        <p className="text-sm text-brand-inkSoft mb-1">Current Status</p>
                                        {getStatusChip(approvalDialog.status)}
                                    </div>
                                    {approvalDialog.approval_remarks && (
                                        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-sm">
                                            <p className="text-sm font-medium text-yellow-800 mb-1">Remarks</p>
                                            <p className="text-sm text-yellow-700">{approvalDialog.approval_remarks}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
